import { ConnectionState, type Room, RoomEvent } from "livekit-client";
import { useEffect } from "react";
import { shouldSubscribeSpatiusBaseRoomPublication } from "./spatius-primary-session-policy";

const AUDIO_TRACK_MUTED_MESSAGE = "Audio track muted - microphone may not be working properly";

type AudioDiagnosticsLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type UseLiveKitAudioTrackDiagnosticsInput = {
  currentRoom: Room;
  isRecording: boolean;
  setConnectionError: (error: string | null) => void;
  setAudioTrackError: React.Dispatch<React.SetStateAction<string | null>>;
  setAudioRetryCount: React.Dispatch<React.SetStateAction<number>>;
  ensureSpatiusPrimaryBaseRoomAudioPlayback: (
    targetRoom: Room,
    reason: string,
    options?: { startPlayback?: boolean }
  ) => Promise<unknown>;
  logger: AudioDiagnosticsLogger;
};

export function useLiveKitAudioTrackDiagnostics({
  currentRoom,
  isRecording,
  setConnectionError,
  setAudioTrackError,
  setAudioRetryCount,
  ensureSpatiusPrimaryBaseRoomAudioPlayback,
  logger,
}: UseLiveKitAudioTrackDiagnosticsInput) {
  useEffect(() => {
    let localAudioUnpublishedTimer: ReturnType<typeof setTimeout> | null = null;
    let localAudioEndedTimer: ReturnType<typeof setTimeout> | null = null;

    const clearLocalAudioUnpublishedTimer = () => {
      if (localAudioUnpublishedTimer) {
        clearTimeout(localAudioUnpublishedTimer);
        localAudioUnpublishedTimer = null;
      }
    };

    const clearLocalAudioEndedTimer = () => {
      if (localAudioEndedTimer) {
        clearTimeout(localAudioEndedTimer);
        localAudioEndedTimer = null;
      }
    };

    const isRoomActiveForAudioRecovery = () =>
      currentRoom.state === ConnectionState.Connected ||
      currentRoom.state === ConnectionState.Reconnecting ||
      currentRoom.state === ConnectionState.SignalReconnecting;

    const onDeviceFailure = (error: Error) => {
      logger.error(error.message, error);
      setConnectionError("Microphone access error. Please check your browser permissions.");
      alert(
        "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
      );
    };

    const onTrackMuted = (track: any, participant: any) => {
      const trackKind = track?.kind;
      const isSpatiusAvatarAudio = shouldSubscribeSpatiusBaseRoomPublication({
        participantIdentity: participant?.identity,
        publicationKind: trackKind,
      });

      if (isSpatiusAvatarAudio) {
        logger.info("Spatius avatar audio muted", {
          participantIdentity: participant?.identity || null,
          trackSid: track?.trackSid || track?.sid || null,
        });
        return;
      }

      if (trackKind === "audio" && participant.isLocal) {
        if (currentRoom.localParticipant.isMicrophoneEnabled === false) {
          logger.info("Local microphone intentionally muted");
          setAudioTrackError((prev) => (prev === AUDIO_TRACK_MUTED_MESSAGE ? null : prev));
          return;
        }

        logger.warn("Local audio track muted unexpectedly - possible silence detection");
        setAudioTrackError(AUDIO_TRACK_MUTED_MESSAGE);
      }
    };

    const onTrackUnmuted = (track: any, participant: any) => {
      const trackKind = track?.kind;
      const isSpatiusAvatarAudio = shouldSubscribeSpatiusBaseRoomPublication({
        participantIdentity: participant?.identity,
        publicationKind: trackKind,
      });

      if (isSpatiusAvatarAudio) {
        logger.info("Spatius avatar audio unmuted", {
          participantIdentity: participant?.identity || null,
          trackSid: track?.trackSid || track?.sid || null,
        });
        return;
      }

      if (trackKind === "audio" && participant.isLocal) {
        logger.info("Local audio track unmuted - audio restored");
        setAudioTrackError(null);
        setAudioRetryCount(0);
      }
    };

    const onLocalTrackPublished = (track: any) => {
      if (track.kind !== "audio") {
        return;
      }

      logger.info("Local audio track published successfully");
      setAudioTrackError(null);
      setAudioRetryCount(0);

      const audioTrack = track.mediaStreamTrack;
      if (!audioTrack) {
        return;
      }

      const handleTrackEnded = () => {
        logger.warn("Audio track ended - possible silence detection or device issue", {
          roomState: currentRoom.state,
        });

        clearLocalAudioEndedTimer();
        localAudioEndedTimer = setTimeout(() => {
          if (!isRecording || !isRoomActiveForAudioRecovery()) {
            logger.info("Suppressing local audio ended error during room teardown", {
              roomState: currentRoom.state,
              wasRecording: isRecording,
            });
            return;
          }

          setAudioTrackError("Audio track stopped - microphone may have stopped working");
        }, 750);
      };

      audioTrack.addEventListener("ended", handleTrackEnded);

      track.on("ended", () => {
        audioTrack.removeEventListener("ended", handleTrackEnded);
        clearLocalAudioEndedTimer();
      });
    };

    const onLocalTrackUnpublished = (track: any) => {
      if (track.kind !== "audio") {
        return;
      }

      logger.warn("Local audio track unpublished - checking for audio issues", {
        roomState: currentRoom.state,
      });

      clearLocalAudioUnpublishedTimer();
      localAudioUnpublishedTimer = setTimeout(() => {
        if (!isRecording || !isRoomActiveForAudioRecovery()) {
          logger.info("Suppressing local audio unpublished error during room teardown", {
            roomState: currentRoom.state,
            wasRecording: isRecording,
          });
          return;
        }

        setAudioTrackError("Audio track disconnected - microphone may have stopped working");
      }, 750);
    };

    const onTrackUnsubscribed = (_track: any, publication: any, participant: any) => {
      const isSpatiusAvatarAudio = shouldSubscribeSpatiusBaseRoomPublication({
        participantIdentity: participant?.identity,
        publicationKind: publication?.kind,
      });

      if (!isSpatiusAvatarAudio) {
        return;
      }

      logger.warn("Spatius avatar audio unsubscribed", {
        participantIdentity: participant?.identity || null,
        roomState: currentRoom.state,
        trackSid: publication?.trackSid || publication?.sid || null,
      });

      if (!isRoomActiveForAudioRecovery()) {
        logger.info("Skipping Spatius avatar audio resubscribe during room teardown", {
          participantIdentity: participant?.identity || null,
          roomState: currentRoom.state,
          trackSid: publication?.trackSid || publication?.sid || null,
        });
        return;
      }

      try {
        publication?.setSubscribed?.(true);
        logger.info("Spatius avatar audio resubscribe requested", {
          participantIdentity: participant?.identity || null,
          trackSid: publication?.trackSid || publication?.sid || null,
          reason: "track_unsubscribed",
        });
        void ensureSpatiusPrimaryBaseRoomAudioPlayback(currentRoom, "track_unsubscribed", {
          startPlayback: true,
        });
      } catch (error) {
        logger.warn("Failed to resubscribe Spatius avatar audio", {
          participantIdentity: participant?.identity || null,
          trackSid: publication?.trackSid || publication?.sid || null,
          reason: "track_unsubscribed",
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    const onTrackSubscriptionFailed = (trackSid: string, participant: any) => {
      const publication = Array.from(participant?.trackPublications?.values?.() ?? []).find(
        (candidate: any) => candidate?.trackSid === trackSid || candidate?.sid === trackSid
      ) as any;
      const isSpatiusAvatarAudio = shouldSubscribeSpatiusBaseRoomPublication({
        participantIdentity: participant?.identity,
        publicationKind: publication?.kind,
      });

      if (!isSpatiusAvatarAudio) {
        return;
      }

      logger.warn("Spatius avatar audio subscription failed", {
        participantIdentity: participant?.identity || null,
        roomState: currentRoom.state,
        trackSid,
      });

      if (!isRoomActiveForAudioRecovery()) {
        logger.info("Skipping Spatius avatar audio resubscribe during room teardown", {
          participantIdentity: participant?.identity || null,
          roomState: currentRoom.state,
          trackSid,
        });
        return;
      }

      try {
        publication?.setSubscribed?.(true);
        logger.info("Spatius avatar audio resubscribe requested", {
          participantIdentity: participant?.identity || null,
          trackSid,
          reason: "subscription_failed",
        });
        void ensureSpatiusPrimaryBaseRoomAudioPlayback(currentRoom, "subscription_failed", {
          startPlayback: true,
        });
      } catch (error) {
        logger.warn("Failed to resubscribe Spatius avatar audio", {
          participantIdentity: participant?.identity || null,
          trackSid,
          reason: "subscription_failed",
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    currentRoom.on(RoomEvent.MediaDevicesError, onDeviceFailure);
    currentRoom.on(RoomEvent.TrackMuted, onTrackMuted);
    currentRoom.on(RoomEvent.TrackUnmuted, onTrackUnmuted);
    currentRoom.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    currentRoom.on(RoomEvent.TrackSubscriptionFailed, onTrackSubscriptionFailed);
    currentRoom.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    currentRoom.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);

    return () => {
      currentRoom.off(RoomEvent.MediaDevicesError, onDeviceFailure);
      currentRoom.off(RoomEvent.TrackMuted, onTrackMuted);
      currentRoom.off(RoomEvent.TrackUnmuted, onTrackUnmuted);
      currentRoom.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
      currentRoom.off(RoomEvent.TrackSubscriptionFailed, onTrackSubscriptionFailed);
      currentRoom.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
      currentRoom.off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
      clearLocalAudioEndedTimer();
      clearLocalAudioUnpublishedTimer();
    };
  }, [
    currentRoom,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    isRecording,
    logger,
    setAudioRetryCount,
    setAudioTrackError,
    setConnectionError,
  ]);
}
